import { format, isValid, toDate } from "date-fns";

const DATE_FORMAT = "EE, dd MMM yyyy";
const TIME_FORMAT = "hh:mm a";
const DATE_TIME_FORMAT = "dd MMM yyyy , hh:mm a";

/**
 * Formats a Date as a localized date string, e.g. "05 Aug 2026".
 */
export function formatDate(value: Date): string {
  return format(value, DATE_FORMAT);
}

/**
 * Formats a Date as a localized time string, e.g. "12:55 AM".
 */
export function formatTime(value: Date): string {
  return format(value, TIME_FORMAT);
}

/**
 * Formats a Date as a localized date and time string,
 * e.g. "14 Apr 2026 , 12:55 AM".
 */
export function formatDateTime(value: Date): string {
  return format(value, DATE_TIME_FORMAT);
}

/**
 * Safely formats a possibly invalid date-like value, returning `fallback`
 * when the value cannot be parsed into a valid date.
 */
export function safeFormatDate(value: unknown, fallback = "-"): string {
  if (value === null || value === undefined || typeof value === "boolean") {
    return fallback;
  }

  const date = toDate(value as Date | string | number);

  if (!isValid(date)) {
    return fallback;
  }

  return format(date, DATE_FORMAT);
}

/**
 * Safely formats a possibly invalid date-like value as time,
 * returning `fallback` when the value cannot be parsed.
 */
export function safeFormatTime(value: unknown, fallback = "-"): string {
  if (value === null || value === undefined || typeof value === "boolean") {
    return fallback;
  }

  const date = toDate(value as Date | string | number);

  if (!isValid(date)) {
    return fallback;
  }

  return format(date, TIME_FORMAT);
}

/**
 * Safely formats a possibly invalid date-like value as date and time,
 * returning `fallback` when the value cannot be parsed.
 */
export function safeFormatDateTime(value: unknown, fallback = "-"): string {
  if (value === null || value === undefined || typeof value === "boolean") {
    return fallback;
  }

  const date = toDate(value as Date | string | number);

  if (!isValid(date)) {
    return fallback;
  }

  return format(date, DATE_TIME_FORMAT);
}